import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { isEqual } from 'lodash';
import { Box } from '@mui/material';

import { LoadingOverlay } from '@occc-app/design-system';
import { FormLayout } from '@occc-app/routes/CampaignComponents/components/FormLayout';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { SurveyStatus } from '@occc/graphql';
import { InAudienceConstraint } from '@occc/feat-constraints';
import {
  GeneralSurveyTargetFormInitialValues,
  GeneralSurveyTargetFormProps,
  GeneralSurveyTargetFormValues,
} from './types';
import { validateTarget } from './validation';

import { FooterActions } from '../FooterActions';
import { SurveyTabs } from '../../constants';

export const TargetForm: React.FC<GeneralSurveyTargetFormProps> = ({
  surveyStatus,
  onSubmit,
  onNextStepClick,
  initialValues,
  chainId,
  onCancelClick,
  loading,
  isEntityExists,
  isUserPermittedToEditSurvey,
}) => {
  const shouldEverythingBeDisabled = Boolean(
    !isUserPermittedToEditSurvey || (surveyStatus && surveyStatus === SurveyStatus.Expired),
  );

  const renderForm: FormRenderProps<GeneralSurveyTargetFormValues>['render'] = React.useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      dirty,
      invalid,
    }: FormRenderProps<GeneralSurveyTargetFormValues>) => {
      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      const actions = (
        <FooterActions
          onNextStepClick={onNextStepClick}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || invalid}
          isEntityExists={isEntityExists}
          currentTab={SurveyTabs.Target}
          isUserPermittedToEditSurvey={isUserPermittedToEditSurvey}
        />
      );

      return (
        <form onSubmit={handleSubmit}>
          <FormLayout actions={actions}>
            <Box p={3}>
              <CanEditFormFields isEditable={!shouldEverythingBeDisabled}>
                <InAudienceConstraint values={values} chainId={chainId} />
              </CanEditFormFields>
            </Box>
          </FormLayout>
        </form>
      );
    },
    [
      onNextStepClick,
      isEntityExists,
      isUserPermittedToEditSurvey,
      shouldEverythingBeDisabled,
      chainId,
      onCancelClick,
    ],
  );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isUserPermittedToEditSurvey}>
        <Form<GeneralSurveyTargetFormValues, GeneralSurveyTargetFormInitialValues>
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={renderForm}
          validate={validateTarget}
          initialValuesEqual={isEqual}
          keepDirtyOnReinitialize
        />
      </CanEditFormFields>

      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
